import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { API_ENDPOINTS } from '../../config/apiRouting';
import { FacebookOAuth } from './FacebookOAuth';
import { buildLinkedInOAuthUrl } from '../../config/socialMediaConfig';
import './SocialMediaConfiguration.css';

interface SocialMediaAccount {
  accountId: string;
  platform: 'facebook' | 'instagram' | 'twitter' | 'linkedin' | 'tiktok';
  accountName: string;
  platformUsername: string;
  profileImageUrl?: string;
  followerCount: number;
  isActive: boolean;
  autoPost: boolean;
  defaultHashtags: string[];
  gtmContainerId?: string;
  trackingPixelId?: string;
  conversionPixelId?: string;
  lastConnected: string;
}

interface PlatformConfig {
  platform: string;
  name: string;
  color: string;
  description: string;
  features: string[];
  setupInstructions: string[];
}

const PLATFORM_CONFIGS: PlatformConfig[] = [
  {
    platform: 'facebook',
    name: 'Facebook',
    color: '#1877f2',
    description: 'Connect your Facebook Page to post updates, promotions, and engage with customers.',
    features: ['Auto-posting', 'Boost posts', 'Analytics', 'Pixel tracking'],
    setupInstructions: [
      'Go to Facebook Developers Console',
      'Create or select your app',
      'Generate Page Access Token',
      'Add your Facebook Page ID',
      'Configure webhooks (optional)'
    ]
  },
  {
    platform: 'instagram',
    name: 'Instagram',
    color: '#E4405F',
    description: 'Share photos and stories to showcase your restaurant and menu items.',
    features: ['Photo/Video posts', 'Stories', 'Shopping tags', 'Insights'],
    setupInstructions: [
      'Connect Instagram Business Account',
      'Link to Facebook Page',
      'Generate Instagram Basic Display API token',
      'Configure Instagram Shopping (optional)'
    ]
  },
  {
    platform: 'twitter',
    name: 'Twitter',
    color: '#1DA1F2',
    description: 'Share quick updates, engage in conversations, and promote special offers.',
    features: ['Text & media posts', 'Threads', 'Promoted tweets', 'Analytics'],
    setupInstructions: [
      'Apply for Twitter Developer Account',
      'Create Twitter App',
      'Generate API Key and Secret',
      'Generate Access Token and Secret',
      'Configure OAuth permissions'
    ]
  },
  {
    platform: 'linkedin',
    name: 'LinkedIn',
    color: '#0077b5',
    description: 'Professional networking and B2B marketing for corporate catering and events.',
    features: ['Company updates', 'Sponsored content', 'Lead generation', 'Analytics'],
    setupInstructions: [
      'Create LinkedIn Company Page',
      'Set up LinkedIn Developer App',
      'Generate Company Access Token',
      'Configure posting permissions'
    ]
  },
  {
    platform: 'tiktok',
    name: 'TikTok',
    color: '#000000',
    description: 'Create viral video content showcasing your food, atmosphere, and behind-the-scenes.',
    features: ['Video posts', 'Trending hashtags', 'Business analytics', 'Advertising'],
    setupInstructions: [
      'Set up TikTok for Business account',
      'Create TikTok Developer App',
      'Generate API access credentials',
      'Configure video upload permissions'
    ]
  }
];

interface SocialMediaConfigurationProps {
  storeId: string;
  storeName: string;
}

export const SocialMediaConfiguration: React.FC<SocialMediaConfigurationProps> = ({ storeId, storeName }) => {
  const { getIdToken } = useAuth();
  const [accounts, setAccounts] = useState<SocialMediaAccount[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showConnectionModal, setShowConnectionModal] = useState(false);
  const [selectedPlatform, setSelectedPlatform] = useState<PlatformConfig | null>(null);
  const [connectionForm, setConnectionForm] = useState({
    accountName: '',
    apiKey: '',
    apiSecret: '',
    refreshToken: '',
    platformAccountId: '',
    platformUsername: '',
    gtmContainerId: '',
    trackingPixelId: '',
    conversionPixelId: '',
    defaultHashtags: '',
    autoPost: false
  });

  useEffect(() => {
    if (storeId) {
      fetchSocialMediaAccounts();
    }
  }, [storeId]);

  const fetchSocialMediaAccounts = async () => {
    if (!storeId) return;
    
    setLoading(true);
    try {
      const token = await getIdToken();
      const response = await fetch(`${API_ENDPOINTS.socialMediaAccounts()}?storeId=${storeId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (!response.ok) throw new Error('Failed to fetch social media accounts');
      const data = await response.json();
      setAccounts(data.data || []);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch social media accounts');
    } finally {
      setLoading(false);
    }
  };

  const handleConnectPlatform = (platform: PlatformConfig) => {
    // LinkedIn uses OAuth flow instead of manual API key entry
    if (platform.platform === 'linkedin') {
      const oauthState = JSON.stringify({ storeId, storeName, timestamp: Date.now() });
      localStorage.setItem('linkedin_oauth_state', oauthState);
      window.location.href = buildLinkedInOAuthUrl(storeId, storeName);
      return;
    }
    setSelectedPlatform(platform);
    setConnectionForm({
      accountName: '',
      apiKey: '',
      apiSecret: '',
      refreshToken: '',
      platformAccountId: '',
      platformUsername: '',
      gtmContainerId: '',
      trackingPixelId: '',
      conversionPixelId: '',
      defaultHashtags: '',
      autoPost: false
    });
    setShowConnectionModal(true);
  };

  const handleSaveConnection = async () => {
    if (!selectedPlatform || !storeId) return;

    try {
      const token = await getIdToken();
      const response = await fetch(API_ENDPOINTS.socialMediaAccounts(), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          storeId: storeId,
          platform: selectedPlatform.platform,
          accountName: connectionForm.accountName,
          apiKey: connectionForm.apiKey,
          apiSecret: connectionForm.apiSecret,
          refreshToken: connectionForm.refreshToken,
          platformAccountId: connectionForm.platformAccountId,
          platformUsername: connectionForm.platformUsername,
          gtmContainerId: connectionForm.gtmContainerId,
          trackingPixelId: connectionForm.trackingPixelId,
          conversionPixelId: connectionForm.conversionPixelId,
          defaultHashtags: connectionForm.defaultHashtags.split(',').map(tag => tag.trim()),
          autoPost: connectionForm.autoPost
        })
      });

      if (!response.ok) throw new Error('Failed to save social media connection');
      
      await fetchSocialMediaAccounts();
      setShowConnectionModal(false);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save connection');
    }
  };

  const handleDisconnectAccount = async (accountId: string) => {
    if (!window.confirm('Are you sure you want to disconnect this account? This will stop all scheduled posts.')) {
      return;
    }

    try {
      const token = await getIdToken();
      const response = await fetch(`${API_ENDPOINTS.socialMediaAccounts()}/${accountId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) throw new Error('Failed to disconnect account');
      
      await fetchSocialMediaAccounts();
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to disconnect account');
    }
  };

  const toggleAutoPost = async (accountId: string, currentAutoPost: boolean) => {
    try {
      const token = await getIdToken();
      const response = await fetch(`${API_ENDPOINTS.socialMediaAccounts()}/${accountId}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          autoPost: !currentAutoPost
        })
      });

      if (!response.ok) throw new Error('Failed to update auto-post setting');
      
      await fetchSocialMediaAccounts();
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update setting');
    }
  };

  const getConnectedPlatforms = () => {
    return accounts.map(account => account.platform);
  };

  const formatNumber = (num: number) => {
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
    return num.toString();
  };

  if (loading && !accounts.length) {
    return <div className="social-media-loading">Loading social media accounts...</div>;
  }

  return (
    <div className="social-media-configuration">
      <div className="page-header">
        <h1>Social Media Management</h1>
        <p>Connect your social media accounts to schedule posts, distribute offers, and track performance.</p>
      </div>

      {error && (
        <div className="error-banner">
          <span>ERROR: {error}</span>
          <button onClick={() => setError(null)}>×</button>
        </div>
      )}

      <div className="connected-accounts-section">
        <h2>Connected Accounts</h2>
        {accounts.length === 0 ? (
          <div className="no-accounts">
            <p>No social media accounts connected yet. Connect your first account below to get started!</p>
          </div>
        ) : (
          <div className="accounts-grid">
            {accounts.map(account => (
              <div key={account.accountId} className="account-card">
                <div className="account-header">
                  <div className="account-info">
                    <div className="platform-name">{account.platform.toUpperCase()}</div>
                    <div>
                      <h3>{account.accountName}</h3>
                      <p>@{account.platformUsername}</p>
                    </div>
                  </div>
                  <div className="account-actions">
                    <button 
                      className={`toggle-autopost ${account.autoPost ? 'active' : ''}`}
                      onClick={() => toggleAutoPost(account.accountId, account.autoPost)}
                      title="Toggle auto-posting"
                    >
                      {account.autoPost ? 'Auto' : 'Manual'}
                    </button>
                    <button 
                      className="disconnect-btn"
                      onClick={() => handleDisconnectAccount(account.accountId)}
                      title="Disconnect account"
                    >
                      Disconnect
                    </button>
                  </div>
                </div>

                <div className="account-stats">
                  <div className="stat">
                    <span className="stat-label">Followers</span>
                    <span className="stat-value">{formatNumber(account.followerCount || 0)}</span>
                  </div>
                  <div className="stat">
                    <span className="stat-label">Status</span>
                    <span className={`stat-value status-${account.isActive ? 'active' : 'inactive'}`}>
                      {account.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                </div>

                {account.defaultHashtags?.length > 0 && (
                  <div className="account-hashtags">
                    <span className="hashtags-label">Default hashtags:</span>
                    <div className="hashtags">
                      {account.defaultHashtags.map((tag, index) => (
                        <span key={index} className="hashtag">#{tag}</span>
                      ))}
                    </div>
                  </div>
                )}

                {account.gtmContainerId && (
                  <div className="tracking-info">
                    <span>GTM: {account.gtmContainerId}</span>
                    {account.trackingPixelId && <span>Pixel: {account.trackingPixelId}</span>}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="available-platforms-section">
        <h2>Connect Social Media Platforms</h2>
        <div className="platforms-grid">
          {/* Facebook with OAuth Integration */}
          <div className="platform-section facebook-section">
            <FacebookOAuth
              storeId={storeId}
              storeName={storeName}
              onConnectionSuccess={() => {
                fetchSocialMediaAccounts();
                setError(null);
              }}
              onConnectionError={(error) => setError(error)}
            />
          </div>

          {/* Other Platforms */}
          {PLATFORM_CONFIGS.filter(platform => platform.platform !== 'facebook').map(platform => {
            const isConnected = getConnectedPlatforms().includes(platform.platform as any);
            return (
              <div key={platform.platform} className={`platform-card ${isConnected ? 'connected' : ''}`}>
                <div className="platform-header">
                  <span className="platform-name" style={{ color: platform.color }}>
                    {platform.name}
                  </span>
                  <h3>{platform.name}</h3>
                  {isConnected && <span className="connected-badge">Connected</span>}
                </div>
                
                <p className="platform-description">{platform.description}</p>
                
                <div className="platform-features">
                  <h4>Features:</h4>
                  <ul>
                    {platform.features.map((feature, index) => (
                      <li key={index}>{feature}</li>
                    ))}
                  </ul>
                </div>

                <button 
                  className={`connect-btn ${isConnected ? 'reconnect' : 'connect'}`}
                  onClick={() => handleConnectPlatform(platform)}
                  style={{ backgroundColor: platform.color }}
                >
                  {isConnected ? 'Reconnect' : 'Connect'} {platform.name}
                </button>
              </div>
            );
          })}
        </div>
      </div>

      {/* Connection Modal */}
      {showConnectionModal && selectedPlatform && (
        <div className="modal-overlay">
          <div className="connection-modal">
            <div className="modal-header">
              <h2>Connect {selectedPlatform.name}</h2>
              <button 
                className="close-btn"
                onClick={() => setShowConnectionModal(false)}
              >
                ×
              </button>
            </div>

            <div className="modal-content">
              <div className="setup-instructions">
                <h3>Setup Instructions:</h3>
                <ol>
                  {selectedPlatform.setupInstructions.map((instruction, index) => (
                    <li key={index}>{instruction}</li>
                  ))}
                </ol>
              </div>

              <div className="connection-form">
                <div className="form-group">
                  <label htmlFor="account-name">Account Display Name *</label>
                  <input
                    id="account-name"
                    type="text"
                    value={connectionForm.accountName}
                    onChange={(e) => setConnectionForm({...connectionForm, accountName: e.target.value})}
                    placeholder="My Restaurant Page"
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="api-key">API Key / Access Token *</label>
                  <input
                    id="api-key"
                    type="password"
                    value={connectionForm.apiKey}
                    onChange={(e) => setConnectionForm({...connectionForm, apiKey: e.target.value})}
                    placeholder="Enter your API key or access token"
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="api-secret">API Secret (if required)</label>
                  <input
                    id="api-secret"
                    type="password"
                    value={connectionForm.apiSecret}
                    onChange={(e) => setConnectionForm({...connectionForm, apiSecret: e.target.value})}
                    placeholder="Enter your API secret"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="platform-username">Platform Username</label>
                  <input
                    id="platform-username"
                    type="text"
                    value={connectionForm.platformUsername}
                    onChange={(e) => setConnectionForm({...connectionForm, platformUsername: e.target.value})}
                    placeholder="@myrestaurant"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="platform-account-id">Platform Account ID</label>
                  <input
                    id="platform-account-id"
                    type="text"
                    value={connectionForm.platformAccountId}
                    onChange={(e) => setConnectionForm({...connectionForm, platformAccountId: e.target.value})}
                    placeholder="123456789"
                  />
                </div>

                <div className="form-section">
                  <h3>Google Tag Manager Integration</h3>
                  <div className="form-group">
                    <label htmlFor="gtm-container">GTM Container ID</label>
                    <input
                      id="gtm-container"
                      type="text"
                      value={connectionForm.gtmContainerId}
                      onChange={(e) => setConnectionForm({...connectionForm, gtmContainerId: e.target.value})}
                      placeholder="GTM-XXXXXXX"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="tracking-pixel">Tracking Pixel ID</label>
                    <input
                      id="tracking-pixel"
                      type="text"
                      value={connectionForm.trackingPixelId}
                      onChange={(e) => setConnectionForm({...connectionForm, trackingPixelId: e.target.value})}
                      placeholder="Facebook Pixel ID or Google Analytics ID"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="conversion-pixel">Conversion Pixel ID</label>
                    <input
                      id="conversion-pixel"
                      type="text"
                      value={connectionForm.conversionPixelId}
                      onChange={(e) => setConnectionForm({...connectionForm, conversionPixelId: e.target.value})}
                      placeholder="Conversion tracking pixel ID"
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="default-hashtags">Default Hashtags (comma-separated)</label>
                  <input
                    id="default-hashtags"
                    type="text"
                    value={connectionForm.defaultHashtags}
                    onChange={(e) => setConnectionForm({...connectionForm, defaultHashtags: e.target.value})}
                    placeholder="restaurant, food, local, dining"
                  />
                </div>

                <div className="form-group checkbox-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={connectionForm.autoPost}
                      onChange={(e) => setConnectionForm({...connectionForm, autoPost: e.target.checked})}
                    />
                    Enable automatic posting
                  </label>
                </div>
              </div>

              <div className="modal-actions">
                <button 
                  className="btn-secondary"
                  onClick={() => setShowConnectionModal(false)}
                >
                  Cancel
                </button>
                <button 
                  className="btn-primary"
                  onClick={handleSaveConnection}
                  disabled={!connectionForm.accountName || !connectionForm.apiKey}
                >
                  Connect Account
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};